import { useState, useRef, useEffect, useCallback } from 'react';

// ─── Unified carousel config ───────────────────────────────────────────────────

export interface CarouselConfig {
  slidesDesktop: number;
  slidesTablet: number;
  slidesMobile: number;
  gap: number;
  loop: boolean;
  autoplay: boolean;
  autoplaySpeed: number;
  pauseOnHover: boolean;
  drag: boolean;
  touch: boolean;
  navigation: boolean;
  pagination: boolean;
}

export const DEFAULT_CAROUSEL_CONFIG: CarouselConfig = {
  slidesDesktop: 4,
  slidesTablet: 2,
  slidesMobile: 1,
  gap: 20,
  loop: false,
  autoplay: false,
  autoplaySpeed: 5000,
  pauseOnHover: true,
  drag: true,
  touch: true,
  navigation: true,
  pagination: true,
};

// Build a CarouselConfig from admin DisplayConfig fields
export function carouselConfigFromDisplay(d?: {
  carousel_slides_desktop?: number;
  carousel_slides_tablet?: number;
  carousel_slides_mobile?: number;
  carousel_space_between?: number;
  carousel_loop?: boolean;
  carousel_autoplay?: boolean;
  carousel_autoplay_speed?: number;
  carousel_pause_on_hover?: boolean;
  carousel_drag?: boolean;
  carousel_touch?: boolean;
  carousel_navigation?: boolean;
  carousel_pagination?: boolean;
}): CarouselConfig {
  return {
    slidesDesktop: d?.carousel_slides_desktop ?? DEFAULT_CAROUSEL_CONFIG.slidesDesktop,
    slidesTablet: d?.carousel_slides_tablet ?? DEFAULT_CAROUSEL_CONFIG.slidesTablet,
    slidesMobile: d?.carousel_slides_mobile ?? DEFAULT_CAROUSEL_CONFIG.slidesMobile,
    gap: d?.carousel_space_between ?? DEFAULT_CAROUSEL_CONFIG.gap,
    loop: d?.carousel_loop ?? DEFAULT_CAROUSEL_CONFIG.loop,
    autoplay: d?.carousel_autoplay ?? DEFAULT_CAROUSEL_CONFIG.autoplay,
    autoplaySpeed: d?.carousel_autoplay_speed ?? DEFAULT_CAROUSEL_CONFIG.autoplaySpeed,
    pauseOnHover: d?.carousel_pause_on_hover ?? DEFAULT_CAROUSEL_CONFIG.pauseOnHover,
    drag: d?.carousel_drag ?? DEFAULT_CAROUSEL_CONFIG.drag,
    touch: d?.carousel_touch ?? DEFAULT_CAROUSEL_CONFIG.touch,
    navigation: d?.carousel_navigation ?? DEFAULT_CAROUSEL_CONFIG.navigation,
    pagination: d?.carousel_pagination ?? DEFAULT_CAROUSEL_CONFIG.pagination,
  };
}

// ─── Hook: responsive device detection ────────────────────────────────────────

export function useDevice() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(() => window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const update = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return { isMobile, isTablet };
}

// ─── SectionCarousel ──────────────────────────────────────────────────────────
//
// GPU-accelerated transform-based carousel.
// No overflow-x, no CSS scroll snapping.
// Matches the visual language (arrows / dots) of the Reviews carousel.

interface SectionCarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  config?: Partial<CarouselConfig>;
  className?: string;
}

export function SectionCarousel<T>({
  items,
  renderItem,
  config,
  className,
}: SectionCarouselProps<T>) {
  const cfg: CarouselConfig = { ...DEFAULT_CAROUSEL_CONFIG, ...config };
  const { isMobile, isTablet } = useDevice();

  const slidesPerView = isMobile
    ? cfg.slidesMobile
    : isTablet
      ? cfg.slidesTablet
      : cfg.slidesDesktop;

  const maxIndex = Math.max(0, items.length - slidesPerView);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Clamp index whenever slidesPerView changes
  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, Math.max(0, items.length - slidesPerView)));
  }, [slidesPerView, items.length]);

  const dragStartX = useRef<number | null>(null);
  const draggingRef = useRef(false); // skips autoplay ticks while drag is active (no re-render needed)
  const [isDragging, setIsDragging] = useState(false);
  const [paused, setPaused] = useState(false);

  // Wrap at maxIndex + 1 (not items.length) so currentIndex never exceeds maxIndex.
  // Exceeding maxIndex with loop=true caused blank space and an immediate autoplay
  // snap back to 0 on the very next tick ("jumps to slide 1" bug).
  const goTo = useCallback(
    (idx: number) => {
      const total = maxIndex + 1;
      setCurrentIndex(
        cfg.loop
          ? ((idx % total) + total) % total
          : Math.max(0, Math.min(idx, maxIndex)),
      );
    },
    [cfg.loop, maxIndex],
  );

  const next = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);
  const prev = useCallback(() => goTo(currentIndex - 1), [currentIndex, goTo]);

  // Autoplay: local interval variable — no intervalRef needed, cleanup via effect return.
  // draggingRef lets us skip ticks mid-drag without tearing down the interval.
  useEffect(() => {
    if (!cfg.autoplay || paused || items.length <= slidesPerView) return;
    const id = setInterval(() => {
      if (draggingRef.current) return;
      setCurrentIndex((i) => (i >= maxIndex ? (cfg.loop ? 0 : i) : i + 1));
    }, cfg.autoplaySpeed);
    return () => clearInterval(id);
  }, [cfg.autoplay, cfg.autoplaySpeed, cfg.loop, maxIndex, items.length, slidesPerView, paused]);

  // Drag / touch handlers
  const handleDragStart = (clientX: number) => {
    dragStartX.current = clientX;
    setIsDragging(false);
    draggingRef.current = true;
  };

  const handleDragEnd = (clientX: number) => {
    draggingRef.current = false;
    if (dragStartX.current === null) return;
    const diff = clientX - dragStartX.current;
    dragStartX.current = null;
    setIsDragging(false);
    if (Math.abs(diff) > 50) {
      if (diff < 0) next();
      else prev();
    }
  };

  const handleDragMove = (clientX: number) => {
    if (dragStartX.current !== null && Math.abs(clientX - dragStartX.current) > 5) {
      setIsDragging(true);
    }
  };

  // Computed geometry
  // Each slide width = calc((100% - gap * (n-1)) / n)
  // Translation to slide N = -N * (slideWidth + gap)
  //   = -N * ((100% - gap*(n-1)) / n + gap)
  //   = -N * (100% + gap) / n
  const gap = cfg.gap;
  const n = slidesPerView;
  // Using CSS calc: translateX(calc(-currentIndex * (100% + gap) / n))
  const translateValue = `calc(${currentIndex} * (-100% - ${gap}px) / ${n})`;

  const canGoPrev = cfg.loop ? items.length > slidesPerView : currentIndex > 0;
  const canGoNext = cfg.loop ? items.length > slidesPerView : currentIndex < maxIndex;
  const dotCount = maxIndex + 1; // always equals number of scroll positions
  const showNav = cfg.navigation && items.length > slidesPerView;
  const showDots = cfg.pagination && dotCount > 1;

  return (
    <div className={`relative ${className ?? ''}`}>
      {/* Track — overflow-hidden so no scrollbar ever appears */}
      <div
        className="overflow-hidden"
        onMouseEnter={() => cfg.pauseOnHover && setPaused(true)}
        onMouseLeave={() => { cfg.pauseOnHover && setPaused(false); dragStartX.current = null; setIsDragging(false); }}
      >
        <div
          className="flex will-change-transform"
          style={{
            transform: `translateX(${translateValue})`,
            gap: `${gap}px`,
            transition: isDragging ? 'none' : 'transform 420ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            cursor: cfg.drag ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
          }}
          onMouseDown={(e) => cfg.drag && handleDragStart(e.clientX)}
          onMouseMove={(e) => cfg.drag && handleDragMove(e.clientX)}
          onMouseUp={(e) => cfg.drag && handleDragEnd(e.clientX)}
          onTouchStart={(e) => cfg.touch && handleDragStart(e.touches[0].clientX)}
          onTouchMove={(e) => cfg.touch && handleDragMove(e.touches[0].clientX)}
          onTouchEnd={(e) => cfg.touch && handleDragEnd(e.changedTouches[0].clientX)}
        >
          {items.map((item, i) => (
            <div
              key={i}
              className="shrink-0"
              style={{
                width: `calc((100% - ${gap * (n - 1)}px) / ${n})`,
              }}
            >
              {renderItem(item, i)}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation arrows — same visual style as Reviews */}
      {showNav && (
        <>
          <button
            type="button"
            onClick={prev}
            disabled={!canGoPrev}
            aria-label="Previous"
            className="absolute -left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md text-ink transition-all duration-300 hover:shadow-lg hover:bg-white disabled:opacity-30 md:-left-5"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canGoNext}
            aria-label="Next"
            className="absolute -right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md text-ink transition-all duration-300 hover:shadow-lg hover:bg-white disabled:opacity-30 md:-right-5"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}

      {/* Pagination dots — same style as Reviews */}
      {showDots && (
        <div className="mt-6 flex justify-center items-center gap-2">
          {Array.from({ length: dotCount }).map((_, p) => {
            const active = p === currentIndex % dotCount;
            return (
              <button
                key={p}
                type="button"
                onClick={() => goTo(p)}
                aria-label={`Go to slide ${p + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  active ? 'w-6 h-1.5 bg-ink' : 'w-1.5 h-1.5 bg-[#C8C2BA] hover:bg-stone'
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── SectionGrid — grid or carousel switchable layout ─────────────────────────
//
// Wraps SectionCarousel and standard CSS grid.
// Reads the admin DisplayConfig fields and renders accordingly.

export interface SectionDisplayConfig {
  display_mode?: 'grid' | 'carousel';
  columns_desktop?: number;
  columns_tablet?: number;
  columns_mobile?: number;
  rows?: number;
  spacing?: 'small' | 'medium' | 'large';
  card_style?: 'standard' | 'minimal' | 'luxury' | 'bordered';
  image_ratio?: 'square' | 'portrait' | 'landscape' | 'original';
  items_limit?: number | 'all';
  carousel_slides_desktop?: number;
  carousel_slides_tablet?: number;
  carousel_slides_mobile?: number;
  carousel_space_between?: number;
  carousel_loop?: boolean;
  carousel_autoplay?: boolean;
  carousel_autoplay_speed?: number;
  carousel_pause_on_hover?: boolean;
  carousel_drag?: boolean;
  carousel_touch?: boolean;
  carousel_navigation?: boolean;
  carousel_pagination?: boolean;
  // Card element visibility toggles
  show_image?: boolean;
  show_title?: boolean;
  show_price?: boolean;
  show_badges?: boolean;
  show_wishlist?: boolean;
  show_quick_view?: boolean;
  show_add_to_cart?: boolean;
  show_rating?: boolean;
  show_discount_badge?: boolean;
}

export const DEFAULT_SECTION_DISPLAY: Required<SectionDisplayConfig> = {
  display_mode: 'grid',
  columns_desktop: 4,
  columns_tablet: 2,
  columns_mobile: 2,
  rows: 2,
  spacing: 'medium',
  card_style: 'standard',
  image_ratio: 'portrait',
  items_limit: 8,
  carousel_slides_desktop: 4,
  carousel_slides_tablet: 2,
  carousel_slides_mobile: 1,
  carousel_space_between: 20,
  carousel_loop: false,
  carousel_autoplay: false,
  carousel_autoplay_speed: 5000,
  carousel_pause_on_hover: true,
  carousel_drag: true,
  carousel_touch: true,
  carousel_navigation: true,
  carousel_pagination: true,
  show_image: true,
  show_title: true,
  show_price: true,
  show_badges: true,
  show_wishlist: true,
  show_quick_view: true,
  show_add_to_cart: true,
  show_rating: true,
  show_discount_badge: true,
};

export function resolveDisplay(d?: SectionDisplayConfig): Required<SectionDisplayConfig> {
  return { ...DEFAULT_SECTION_DISPLAY, ...d };
}

// ─── Card style class from display config ─────────────────────────────────────

export function cardWrapperClass(cfg: Required<SectionDisplayConfig>): string {
  switch (cfg.card_style) {
    case 'bordered': return 'ring-1 ring-stone-200 rounded-sm p-0.5';
    case 'luxury':   return 'shadow-lg rounded-sm overflow-hidden';
    case 'minimal':  return 'opacity-90';
    default:         return '';
  }
}

// ─── Image aspect class from display config ───────────────────────────────────

export function imageAspectClass(cfg: Required<SectionDisplayConfig>): string {
  switch (cfg.image_ratio) {
    case 'square':    return 'aspect-square';
    case 'landscape': return 'aspect-[4/3]';
    case 'original':  return '';
    default:          return 'aspect-[4/5]';
  }
}

interface SectionLayoutProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  display?: SectionDisplayConfig;
  className?: string;
  wrapCard?: boolean;
}

export function SectionLayout<T>({
  items,
  renderItem,
  display,
  className,
  wrapCard = true,
}: SectionLayoutProps<T>) {
  const cfg = resolveDisplay(display);
  const { isMobile, isTablet } = useDevice();

  const cols = isMobile
    ? cfg.columns_mobile
    : isTablet
      ? cfg.columns_tablet
      : cfg.columns_desktop;

  const gapPx = cfg.spacing === 'small' ? 12 : cfg.spacing === 'large' ? 28 : 20;
  const wrapClass = wrapCard ? cardWrapperClass(cfg) : '';

  // Limit items for grid
  const maxItems =
    cfg.display_mode === 'grid' && cfg.items_limit !== 'all'
      ? cfg.rows * cols
      : items.length;
  const visibleItems = cfg.display_mode === 'grid' ? items.slice(0, maxItems) : items;

  const wrappedItem = (item: T, i: number) =>
    wrapClass ? (
      <div key={i} className={wrapClass}>{renderItem(item, i)}</div>
    ) : (
      renderItem(item, i)
    );

  if (cfg.display_mode === 'carousel') {
    return (
      <SectionCarousel
        items={items}
        renderItem={(item, i) => wrappedItem(item, i)}
        config={carouselConfigFromDisplay(cfg)}
        className={className}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: `${gapPx}px`,
      }}
    >
      {visibleItems.map((item, i) => wrappedItem(item, i))}
    </div>
  );
}
